import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedDate } from 'react-intl';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import relativeTime from '@afosto/utils/date/relativeTime';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { conversations as conversationsApi } from '@afosto/messaging/api/Conversations';
import ConversationForm from '../ConversationForm';
import uuid from '@afosto/utils/uuid';
import translations from './translations';

const ConversationList = ({ constraints, canCreateComment, entityType, entityID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['mes:conversations:read']);
  const [conversation, setConversation] = useState(null);
  const [isFetchingConversation, setIsFetchingConversation] = useState(true);

  const sessionManager = useSessionManager();
  const { user = {} } = sessionManager ?? {};
  const { enqueueSnackbar } = useSnackbar();

  const handleCreateConversation = async ({ content }) => {
    try {
      let activeConversation = { ...(conversation || {}) };

      if (!conversation || !conversation.id) {
        const response = await conversationsApi.startConversation({
          name: 'Comments',
          event: 'update',
          entity_type: entityType,
          entity_id: entityID,
          participant: {
            name: user?.name,
            reference: user?.email,
            is_machine: false,
          },
          expires_at: new Date(),
        });

        activeConversation = response.data;
      }

      const participant = findObjectInArray(
        activeConversation.participants || [],
        'reference',
        user?.email,
      );

      if (!participant) {
        await conversationsApi.addRegistration(activeConversation.id, {
          name: user?.name,
          reference: user?.email,
          is_machine: false,
        });
      }

      const response = await conversationsApi.addMessage(activeConversation.id, {
        content,
        reference: user?.email,
      });
      const { data } = response;

      enqueueSnackbar(
        intl.formatMessage(translations.conversationList.feedback.conversationCreated),
        {
          variant: 'success',
        },
      );

      setConversation(data);
    } catch (err) {
      enqueueSnackbar(
        intl.formatMessage(translations.conversationList.feedback.conversationNotCreated),
        {
          variant: 'error',
        },
      );
    }
  };

  useEffect(() => {
    let isMounted = true;

    /**
     * Fetch all conversations for an entity.
     * @returns {Promise<[]|Array>}
     */
    const fetchConversations = async () => {
      setIsFetchingConversation(true);

      try {
        const response = await conversationsApi.searchConversations({
          constraints,
        });
        const { data = [] } = response;
        const [firstConversation] = data;

        if (isMounted) {
          setConversation(firstConversation);
        }
      } catch (err) {
        if (isMounted) {
          setConversation(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingConversation(false);
        }
      }
    };

    fetchConversations();

    return () => {
      isMounted = false;
    };
  }, [constraints]);

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.conversationList.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <LoadingWrapper isLoading={isFetchingConversation}>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.conversationList.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          {conversation ? (
            <>
              <Typography variant="overline" color="textSecondary">
                <FormattedDate
                  value={conversation.started_at}
                  year="numeric"
                  month="long"
                  day="numeric"
                  hour="numeric"
                  minute="numeric"
                />
              </Typography>
              <Grid container spacing={1} direction="column">
                {(conversation.messages || []).map(message => {
                  const participant = findObjectInArray(
                    conversation.participants,
                    'reference',
                    message.participant_reference,
                  );

                  return (
                    <Grid item xs={12} key={uuid()}>
                      <Box display="flex">
                        <Avatar
                          src={`https://ui-avatars.com/api/?name=${encodeURI(participant.name)}`}
                        />
                        <Box mx={1} flex={1}>
                          <Card elevation={1} style={{ backgroundColor: 'rgba(0, 0, 150, .05)' }}>
                            <CardContent>
                              <Typography variant="body2">{participant.name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {relativeTime(message.created_at)}
                              </Typography>
                              <Typography variant="body1">{message.content}</Typography>
                            </CardContent>
                          </Card>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Typography variant="subtitle1">
              {intl.formatMessage(translations.conversationList.emptyMessage)}
            </Typography>
          )}
          {canCreateComment && hasPermissions(['mes:conversations:write']) && (
            <ConversationForm onSubmit={handleCreateConversation} />
          )}
        </CardContent>
      </Card>
    </LoadingWrapper>
  );
};

ConversationList.propTypes = {
  constraints: PropTypes.array.isRequired,
  canCreateComment: PropTypes.bool,
  entityType: PropTypes.string,
  entityID: PropTypes.string,
};

ConversationList.defaultProps = {
  canCreateComment: false,
  entityType: null,
  entityID: null,
};

export default ConversationList;
